import React, { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import axios from "axios";
import "./Notifications.css";
import { FiFilter, FiX, FiEye } from "react-icons/fi";
import { TbHexagonLetterJ } from "react-icons/tb";

const Notifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [filters, setFilters] = useState({
        title: "",
        content: "",
        mcap_low: 500,
        mcap_high: 10000000
    });
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]); // Default to current date
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const notificationsPerPage = 10;
    const [loadingSummaries, setLoadingSummaries] = useState({});
    const [summaryModal, setSummaryModal] = useState({ isOpen: false, content: "" });
    const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
    const [noteText, setNoteText] = useState("");
    const [currentNotif, setCurrentNotif] = useState(null);
    const [showFilterOptions, setShowFilterOptions] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState(["title", "content"]); // Default filters
    const [isKeywordModalOpen, setIsKeywordModalOpen] = useState(false);
    const [keywordType, setKeywordType] = useState("positive");
    const [keywordInput, setKeywordInput] = useState("");
    const [positiveKeywords, setPositiveKeywords] = useState([]);
    const [negativeKeywords, setNegativeKeywords] = useState([]);
    const [isViewKeywordModalOpen, setIsViewKeywordModalOpen] = useState(false);
    const [viewKeywords, setViewKeywords] = useState([]);
    const [watchlistEnabled, setWatchlistEnabled] = useState(false);
    // Watchlist Modal State
    const [watchlistModalOpen, setWatchlistModalOpen] = useState(false);
    const [watchlistItems, setWatchlistItems] = useState([]);
    // Add Watchlist Modal State
    const [isAddWatchlistModalOpen, setIsAddWatchlistModalOpen] = useState(false);
    const [newWatchlistEntry, setNewWatchlistEntry] = useState("");
    // Fetch and show the watchlist modal
    const handleViewWatchlist = async () => {
        try {
            const backendHost = window.location.hostname;
            const isSecure = window.location.protocol === "https:";
            const backendUrl = isSecure
                ? `https://${backendHost}`
                : `http://${backendHost}:5001`;
            const response = await axios.get(`${backendUrl}/api/fetch_watchlist`);
            if (response.status === 200) {
                setWatchlistItems(response.data.watchlist || []);
                setWatchlistModalOpen(true);
            }
        } catch (error) {
            console.error("Failed to fetch watchlist:", error);
            alert("Unable to load watchlist.");
        }
    };


    useEffect(() => {
        fetchKeywords("positive");
        fetchKeywords("negative");
    }, []);

    const fetchKeywords = async (type) => {
        try {
            const backendHost = window.location.hostname;
            const isSecure = window.location.protocol === "https:";
            const backendUrl = isSecure
                ? `https://${backendHost}`
                : `http://${backendHost}:5001`;

            const response = await axios.get(`${backendUrl}/api/get_notification_filter_kw?keyword_type=${type}`);

            if (response.status === 200) {
                if (type === "positive") {
                    setPositiveKeywords(response.data.keywords);
                } else {
                    setNegativeKeywords(response.data.keywords);
                }
            }
        } catch (error) {
            console.error(`Error fetching ${type} keywords:`, error);
        }
    };

    const handleDeleteKeyword = async (keyword) => {
        try {
            const backendHost = window.location.hostname;
            const isSecure = window.location.protocol === "https:";
            const backendUrl = isSecure
                ? `https://${backendHost}`
                : `http://${backendHost}:5001`;

            await axios.post(`${backendUrl}/api/delete_notification_filter_kw`, {
                keyword_type: keywordType,
                keyword_name: keyword
            });

            alert(`Deleted keyword: ${keyword}`);
            // Remove the deleted keyword from the local state immediately
            setViewKeywords(prevKeywords => prevKeywords.filter(kw => kw !== keyword));
            fetchKeywords(keywordType);
        } catch (error) {
            console.error("Error deleting keyword:", error);
            alert("Failed to delete keyword. Please try again.");
        }
    };


    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const clearSearch = () => {
        setSearchTerm("");
    };

    const openKeywordModal = (type) => {
        setKeywordType(type);
        setKeywordInput("");
        setIsKeywordModalOpen(true);
    };

    const closeKeywordModal = () => {
        setIsKeywordModalOpen(false);
    };

    const openViewKeywordModal = (type) => {
        setKeywordType(type);
        setViewKeywords(type === "positive" ? positiveKeywords : negativeKeywords);
        setIsViewKeywordModalOpen(true);
    };

    const closeViewKeywordModal = () => {
        setIsViewKeywordModalOpen(false);
    };

    const handleSaveKeywords = async () => {
        const keywords = keywordInput.split(/[,|\n]+/).filter(Boolean);
        console.log(keywords);

        try {
            const backendHost = window.location.hostname;
            const isSecure = window.location.protocol === "https:";
            const backendUrl = isSecure
                ? `https://${backendHost}`
                : `http://${backendHost}:5001`;

            const response = await axios.post(`${backendUrl}/api/add_notification_filter_kw`, {
                keyword_type: keywordType,
                keywords: keywords
            });

            if (response.status === 200) {
                alert(`Successfully added ${keywords.length} ${keywordType} keywords.`);
                closeKeywordModal();
            }
        } catch (error) {
            console.error("Error saving keywords:", error);
            alert("Failed to save keywords. Please try again.");
        }
    };

    const toggleFilterOptions = () => {
        setShowFilterOptions((prev) => !prev);
    };

    const handleFilterSelection = (field) => {
        setSelectedFilters((prev) =>
            prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
        );
    };

    const filteredNotifications = notifications.filter((notif) => {
        if (!searchTerm && !selectedFilters.includes("good keywords") && !selectedFilters.includes("bad keywords")) return true;

        const matchSearchTerm = searchTerm ? notif.title.toLowerCase().includes(searchTerm.toLowerCase()) || notif.content.toLowerCase().includes(searchTerm.toLowerCase()) : true;

        const matchPositive = selectedFilters.includes("good keywords") ? positiveKeywords.some(kw => notif.title.toLowerCase().includes(kw.toLowerCase()) || notif.content.toLowerCase().includes(kw.toLowerCase())) : true;

        const matchNegative = selectedFilters.includes("bad keywords") ? negativeKeywords.some(kw => notif.title.toLowerCase().includes(kw.toLowerCase()) || notif.content.toLowerCase().includes(kw.toLowerCase())) : true;

        return matchSearchTerm && matchPositive && matchNegative;
    });

    const openNoteModal = (notif) => {
        setCurrentNotif(notif);
        setIsNoteModalOpen(true);
    };

    const closeNoteModal = () => {
        setIsNoteModalOpen(false);
        setNoteText("");  // Clear note text when modal closes
        setCurrentNotif(null);
    };

    const [saving, setSaving] = useState(false); // Spinner state

    const handleSaveNote = async () => {
        if (!currentNotif) return;

        setSaving(true); // Show spinner

        try {
            const backendHost = window.location.hostname;
            const isSecure = window.location.protocol === "https:";
            const backendUrl = isSecure
                ? `https://${backendHost}`
                : `http://${backendHost}:5001`;

            const response = await fetch(`${backendUrl}/api/notifications/save_note`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    partition_key: selectedDate.replace(/-/g, ""),
                    sort_key: currentNotif.id,
                    note_text: noteText
                })
            });

            const data = await response.json();
            setSaving(false); // Hide spinner

            if (response.ok) {
                alert("Note saved successfully!");
                closeNoteModal();
            } else {
                alert("Error: " + (data.error || "Failed to save note."));
            }
        } catch (error) {
            setSaving(false);
            console.error("Save note error:", error);
            alert("Failed to save note. Please try again.");
        }
    };

    const [viewingNoteId, setViewingNoteId] = useState(null); // Track which note is being viewed
    const [noteContent, setNoteContent] = useState(""); // Stores fetched note content
    const [showViewNoteModal, setShowViewNoteModal] = useState(false); // Controls modal visibility

    const handleViewNote = async (notif) => {
        setViewingNoteId(notif.id); // Set the loading state for the specific notification

        try {
            const backendHost = window.location.hostname;
            const isSecure = window.location.protocol === "https:";
            const backendUrl = isSecure
                ? `https://${backendHost}`
                : `http://${backendHost}:5001`;

            const response = await axios.post(`${backendUrl}/api/notifications/view_note`, {
                partition_key: selectedDate.replace(/-/g, ""),
                sort_key: notif.id
            });

            if (response.status === 200 && response.data.note_text) {
                setNoteContent(response.data.note_text);
                setShowViewNoteModal(true); // Open modal
            } else {
                alert("No saved note found for this notification.");
            }
        } catch (error) {
            console.error("View note error:", error);
            alert("No saved note found for this notification.");
        } finally {
            setViewingNoteId(null); // Reset loading state
        }
    };



    const fetchSummary = async (pdfUrl, notifId) => {
        setLoadingSummaries(prevState => ({ ...prevState, [notifId]: true }));

        try {
            const backendHost = window.location.hostname;
            const isSecure = window.location.protocol === "https:";
            const backendUrl = isSecure
                ? `https://${backendHost}`
                : `http://${backendHost}:5001`;

            const response = await axios.post(`${backendUrl}/api/summarize_pdf`, { pdf_url: pdfUrl });

            setSummaryModal({ isOpen: true, content: response.data.summary || "No summary available" });
        } catch (error) {
            setSummaryModal({ isOpen: true, content: "Error fetching summary. Please try again later." });
        } finally {
            setLoadingSummaries(prevState => ({ ...prevState, [notifId]: false }));
        }
    };

    const closeSummaryModal = () => {
        setSummaryModal({ isOpen: false, content: "" });
    };

    const formatToISO = (dateString) => {
        if (!dateString) return new Date().toISOString();
        const [day, monthStr, year, time] = dateString.split(/[-\s]/);
        const [hour, minute, second] = time.split(":");
        const monthMap = {
            Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
            Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
        };
        return new Date(
            Date.UTC(year, monthMap[monthStr], day, hour, minute, second)
        ).toISOString();
    };

    const updateNotification = async (notifId, field, value) => {
        try {
            const backendHost = window.location.hostname;
            const isSecure = window.location.protocol === "https:";
            const backendUrl = isSecure
                ? `https://${backendHost}`
                : `http://${backendHost}:5001`;
            const response = await axios.post(`${backendUrl}/api/notifications/update`, {
                partition_key: selectedDate.replace(/-/g, ""),
                sort_key: notifId,
                [field]: value
            });

            if (response.status === 200) {
                setNotifications(notifications.map(notif =>
                    notif.id === notifId ? { ...notif, [field]: value } : notif
                ));
            }
        } catch (error) {
            console.error("Error updating notification:", error);
        }
    };

    const fetchNotifications = async (date, mcapLow = 500, mcapHigh = 10000000, watchlistOnly = false) => {
        setIsLoading(true);
        setError(null);

        try {
            const backendHost = window.location.hostname;
            const isSecure = window.location.protocol === "https:";
            const backendUrl = isSecure
                ? `https://${backendHost}`
                : `http://${backendHost}:5001`;
            const formattedDate = date.replace(/-/g, "");
            const response = await axios.post(`${backendUrl}/api/notifications`, {
                date: formattedDate,
                mcap_low: mcapLow,
                mcap_high: mcapHigh,
                watchlist_only: watchlistOnly
            });

            const formattedNotifications = response.data.notifications
                .filter(notif => !notif.is_ignored) // Filter out notifications with null details
                .map((notif, index) => {
                    const isBSE = notif.exchange === "BSE";
                    return {
                        id: notif.id || index,
                        title: isBSE
                            ? "BSE: " + (notif.details.HEADLINE || "No Title")
                            : "NSE: " + (notif.details.symbol ? notif.details.symbol + " - " : "") + (notif.details.desc || "No Title"),
                        content: isBSE
                            ? notif.details.NEWSSUB || "No Description"
                            : notif.details.attchmntText || "No Description",
                        timestamp: isBSE
                            ? new Date(notif.details.News_submission_dt).toISOString() || new Date().toISOString()
                            : formatToISO(notif.details.an_dt) || new Date().toISOString(),
                        attachmentUrl: notif.attachmentUrl || null, // Store the attachment URL
                        is_read: notif.is_read ?? false,
                        is_important: notif.is_important ?? false,
                        note_url: notif.note_url,
                        exchange: notif.exchange,
                        symbol: notif.symbol
                    };
                });

            setNotifications(formattedNotifications);
        } catch (error) {
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleIgnoreNotification = async (notifId) => {
        try {
            const backendHost = window.location.hostname;
            const isSecure = window.location.protocol === "https:";
            const backendUrl = isSecure
                ? `https://${backendHost}`
                : `http://${backendHost}:5001`;

            const response = await axios.post(`${backendUrl}/api/notifications/update`, {
                partition_key: selectedDate.replace(/-/g, ""),
                sort_key: notifId,
                is_ignored: true
            });

            if (response.status === 200) {
                setNotifications(notifications.filter(notif => notif.id !== notifId)); // Remove ignored notification
            }
        } catch (error) {
            console.error("Error ignoring notification:", error);
            alert("Failed to ignore notification.");
        }
    };



    // Handle date selection without auto-fetching data
    const handleDateChange = (e) => {
        setSelectedDate(e.target.value);
    };

    // Fetch notifications when button is clicked
    const handleFetchClick = () => {
        fetchNotifications(selectedDate, filters.mcap_low, filters.mcap_high, watchlistEnabled);
        setCurrentPage(1); // Reset to the first page when fetching new data
    };

    const todayDate = new Date().toISOString().split("T")[0];

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters({ ...filters, [name]: value });
    };

    const handleViewAttachment = (url) => {
        window.open(url, "_blank"); // Open attachment in a new tab
    };

    const indexOfLastNotification = currentPage * notificationsPerPage;
    const indexOfFirstNotification = indexOfLastNotification - notificationsPerPage;
    const currentNotifications = filteredNotifications.slice(
        indexOfFirstNotification,
        indexOfLastNotification
    );

    const totalPages = Math.ceil(filteredNotifications.length / notificationsPerPage);

    const goToFirstPage = () => setCurrentPage(1);
    const goToLastPage = () => setCurrentPage(totalPages);
    const goToNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
    const goToPreviousPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
    const goToPage = (page) => setCurrentPage(page);

    return (
        <div className="notifications-container">
            <div className="header-container">
                <h1 className="page-title">FinWeb Notifications</h1>
                <div className="top-right-buttons" style={{ display: "flex", gap: "10px" }}>
                    <button
                        className="watchlist-show-btn"
                        style={{
                            backgroundColor: "#dc3545",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            padding: "8px 12px",
                            fontWeight: "bold",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px"
                        }}
                        onClick={handleViewWatchlist}
                    >
                        <FiEye />
                    </button>
                    <button
                        className="watchlist-add-btn"
                        style={{
                            backgroundColor: "#6f42c1",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            padding: "8px 12px",
                            fontWeight: "bold",
                            cursor: "pointer"
                        }}
                        onClick={() => setIsAddWatchlistModalOpen(true)}
                    >
                        Watchlist +
                    </button>
                    <button className="positive-keywords-btn" onClick={() => openKeywordModal("positive")}>Add +ve KWs</button>
                    <button className="negative-keywords-btn" onClick={() => openKeywordModal("negative")}>Add -ve KWs</button>
                    <button className="view-positive-keywords-btn" onClick={() => openViewKeywordModal("positive")}>View +ve KWs</button>
                    <button className="view-negative-keywords-btn" onClick={() => openViewKeywordModal("negative")}>View -ve KWs</button>
                </div>
            </div>

            <div className="filters">
                <div
                  className="filters-row"
                  style={{
                    display: "flex",
                    gap: "12px",
                    alignItems: "center",
                    flexWrap: "wrap"
                  }}
                >
                  <div
                    className="search-container"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flex: "1",
                      maxWidth: "820px"
                    }}
                  >
                    <div className="search-bar-wrapper" style={{ flex: 1 }}>
                      <input
                        type="text"
                        placeholder="Search notifications..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="search-bar"
                        style={{ width: "750px" }}
                      />
                      {searchTerm && (
                        <FiX className="search-clear-icon" size={20} onClick={clearSearch} />
                      )}
                    </div>
                    <button className="filter-button" style={{ flex: "0 0 30px" }} onClick={toggleFilterOptions}>
                      <FiFilter size={20} />
                    </button>
                    {showFilterOptions && (
                      <div className="filter-dropdown">
                        <div className="filter-row">
                          <label>
                            <input
                              type="checkbox"
                              checked={selectedFilters.includes("title")}
                              onChange={() => handleFilterSelection("title")}
                            />
                            Title
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={selectedFilters.includes("content")}
                              onChange={() => handleFilterSelection("content")}
                            />
                            Content
                          </label>
                        </div>
                        <div className="filter-row">
                          <label>
                            <input
                              type="checkbox"
                              checked={selectedFilters.includes("good keywords")}
                              onChange={() => handleFilterSelection("good keywords")}
                            />
                            +veK
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={selectedFilters.includes("bad keywords")}
                              onChange={() => handleFilterSelection("bad keywords")}
                            />
                            -veK
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="top-controls">
                    <input
                      type="date"
                      name="date"
                      value={selectedDate}
                      onChange={handleDateChange}
                      className="date-picker"
                      max={todayDate} // Disable future dates
                    />
                    <div style={{
                      display: "flex",
                      flexDirection: "column",
                      padding: "8px 12px",
                      backgroundColor: "#f9f9f9",
                      borderRadius: "8px",
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                      border: "1px solid #ddd",
                      minWidth: "240px"
                    }}>
                      <label style={{
                        fontWeight: "bold",
                        fontSize: "14px",
                        marginBottom: "6px",
                        color: "#006699"
                      }}>
                        Market Cap Filter (₹ Cr)
                      </label>
                      <div style={{ display: "flex", gap: "6px" }}>
                        <input
                          type="number"
                          placeholder="Min"
                          className="mcap-input"
                          value={filters.mcap_low || ""}
                          onChange={(e) => setFilters({ ...filters, mcap_low: e.target.value })}
                          style={{
                            padding: "6px 10px",
                            borderRadius: "4px",
                            border: "1px solid #ccc",
                            width: "100px"
                          }}
                        />
                        <input
                          type="number"
                          placeholder="Max"
                          className="mcap-input"
                          value={filters.mcap_high || ""}
                          onChange={(e) => setFilters({ ...filters, mcap_high: e.target.value })}
                          style={{
                            padding: "6px 10px",
                            borderRadius: "4px",
                            border: "1px solid #ccc",
                            width: "100px"
                          }}
                        />
                      </div>
                    </div>
                    <button className="fetch-btn" onClick={handleFetchClick}>
                      Fetch Notifications
                    </button>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", marginLeft: "1px" }}>
                      <span style={{ fontSize: "14px", marginBottom: "4px" }}>Watchlist</span>
                      <label style={{ position: "relative", display: "inline-block", width: "52px", height: "28px" }}>
                        <input
                          type="checkbox"
                          checked={watchlistEnabled}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            setWatchlistEnabled(newValue);
                            fetchNotifications(selectedDate, filters.mcap_low, filters.mcap_high, newValue);
                            setCurrentPage(1);
                          }}
                          style={{ opacity: 0, width: 0, height: 0 }}
                        />
                        <span style={{
                          position: "absolute",
                          cursor: "pointer",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: watchlistEnabled ? "#4caf50" : "#ccc",
                          transition: ".4s",
                          borderRadius: "28px"
                        }}>
                          <span style={{
                            position: "absolute",
                            content: '""',
                            height: "24px",
                            width: "24px",
                            left: watchlistEnabled ? "26px" : "2px",
                            bottom: "2px",
                            backgroundColor: "white",
                            transition: ".4s",
                            borderRadius: "50%"
                          }}></span>
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
            </div>

            {isLoading ? (
                <p>Loading data...</p>
            ) : error ? (
                <p className="error">{error}</p>
            ) : (
                <>
                    <ul className="notifications-list">
                        {currentNotifications.map((notif) => (
                            <li key={notif.id}
                                className={`notification-item ${notif.is_read ? "read" : ""} ${notif.is_important ? "important" : ""}`}
                            >
                                <div className="notification-header">
                                    <h3>{notif.title}</h3>
                                </div>
                                <p>{notif.content}</p>
                                <small>{new Date(notif.timestamp).toLocaleString()}</small>
                                <div className="notification-actions">
                                    <button onClick={() => handleViewAttachment(notif.attachmentUrl)}
                                            disabled={!notif.attachmentUrl}>
                                        View Attachment
                                    </button>
                                    <button onClick={() => fetchSummary(notif.attachmentUrl, notif.id)}
                                            disabled={!notif.attachmentUrl}>
                                        Summarize
                                    </button>
                                    {loadingSummaries[notif.id] && <div className="loading-spinner"></div>}
                                    <button onClick={() => updateNotification(notif.id, "is_read", true)}
                                            disabled={notif.is_read}>
                                        Mark as Read
                                    </button>
                                    <button
                                        onClick={() => updateNotification(notif.id, "is_important", !notif.is_important)}>
                                        {notif.is_important ? "Unmark Important" : "Mark as Important"}
                                    </button>
                                    <button onClick={() => openNoteModal(notif)}>Save Note</button>
                                    <button onClick={() => handleViewNote(notif)} disabled={viewingNoteId === notif.id}>
                                        {viewingNoteId === notif.id ? (
                                            <>
                                                <div className="spinner"></div>
                                                Loading...
                                            </>
                                        ) : (
                                            "View Note"
                                        )}
                                    </button>
                                    <button
                                      onClick={async () => {
                                        const instrumentName = `${notif.exchange}:${notif.symbol}`;
                                        try {
                                          const backendHost = window.location.hostname;
                                          const isSecure = window.location.protocol === "https:";
                                          const backendUrl = isSecure
                                            ? `https://${backendHost}`
                                            : `http://${backendHost}:5001`;

                                          const response = await axios.post(`${backendUrl}/api/append_to_watchlist`, {
                                            instrument_name: instrumentName
                                          });

                                          alert(response.data.message || "Instrument added.");
                                        } catch (error) {
                                          console.error("Error adding to watchlist:", error);
                                          alert("Failed to add to watchlist.");
                                        }
                                      }}
                                      style={{
                                        backgroundColor: "#17a2b8",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: "4px",
                                        padding: "6px 12px",
                                        marginRight: "6px",
                                        cursor: "pointer"
                                      }}
                                    >
                                      Watchlist +
                                    </button>
                                    <button className="ignore-btn" onClick={() => handleIgnoreNotification(notif.id)}>
                                        Ignore
                                    </button>

                                </div>

                            </li>
                        ))}
                    </ul>

                    {isNoteModalOpen && (
                        <div className="note-modal">
                            <h2>Save Note</h2>
                            <textarea
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value)}
                                placeholder="Enter your note here..."
                            />
                            <div className="modal-actions">
                                <button onClick={closeNoteModal}>Cancel</button>
                                <button onClick={handleSaveNote} disabled={saving}>
                                    {saving ? (
                                        <>
                                            <div className="spinner"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        "Save"
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                    {showViewNoteModal && (
                        <div className="note-modal">
                            <h2>View Note</h2>
                            <textarea value={noteContent} readOnly/>
                            <div className="modal-actions">
                                <button onClick={() => setShowViewNoteModal(false)}>Close</button>
                            </div>
                        </div>
                    )}


                    <div className="pagination-container">
                        <div className="pagination-controls">
                            <button onClick={goToFirstPage} disabled={currentPage === 1}>First</button>
                            <button onClick={goToPreviousPage} disabled={currentPage === 1}>Prev</button>
                            <button onClick={goToNextPage} disabled={currentPage === totalPages}>Next</button>
                            <button onClick={goToLastPage} disabled={currentPage === totalPages}>Last</button>
                        </div>

                        <div className="pagination-scroll-container">
                            <div className="pagination-page-numbers">
                                {Array.from({length: totalPages}, (_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => goToPage(index + 1)}
                                        className={currentPage === index + 1 ? "active" : ""}
                                    >
                                        {index + 1}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                </>
            )}
            {summaryModal.isOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>Summary</h2>
                        <div className="markdown-container">
                            <ReactMarkdown>{summaryModal.content}</ReactMarkdown>
                        </div>
                        <button onClick={closeSummaryModal}>Close</button>
                    </div>
                </div>
            )}
            {isKeywordModalOpen && (
                <div className="modal-overlay" style={{ position: "fixed", top: "0", left: "0", width: "100vw", height: "100vh", backgroundColor: "rgba(0, 0, 0, 0.5)", display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <div className="modal-content" style={{ width: "70vw", height: "70vh", backgroundColor: "#f0f0f0", padding: "20px", borderRadius: "8px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                        <h2>Add {keywordType === "positive" ? "+ve" : "-ve"} Keywords</h2>
                        <textarea
                            value={keywordInput}
                            onChange={(e) => setKeywordInput(e.target.value)}
                            placeholder="Enter keywords separated by commas or new lines..."
                            style={{ width: "95%", height: "70%", padding: "10px", fontSize: "16px" }}
                        />
                        <div className="modal-actions">
                            <button onClick={closeKeywordModal}>Cancel</button>
                            <button onClick={handleSaveKeywords}>Save</button>
                        </div>
                    </div>
                </div>
            )}
            {isViewKeywordModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>{keywordType === "positive" ? "Positive" : "Negative"} Keywords</h2>
                        <ul className="keyword-list">
                            {viewKeywords.map((keyword, index) => (
                                <li key={index} className="keyword-item">
                                    {keyword}
                                    <button className="delete-keyword-btn"
                                            onClick={() => handleDeleteKeyword(keyword)}>Delete
                                    </button>
                                </li>
                            ))}
                        </ul>
                        <div className="modal-actions">
                            <button onClick={closeViewKeywordModal}>Close</button>
                        </div>
                    </div>
                </div>
            )}
            {watchlistModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>Watchlist</h2>
                        <ul className="keyword-list">
                            {watchlistItems.map((item, index) => (
                                <li
                                    key={index}
                                    className="keyword-item"
                                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                                >
                                    {item}
                                    <button
                                        onClick={async () => {
                                            try {
                                                const backendHost = window.location.hostname;
                                                const isSecure = window.location.protocol === "https:";
                                                const backendUrl = isSecure
                                                    ? `https://${backendHost}`
                                                    : `http://${backendHost}:5001`;
                                                await axios.post(`${backendUrl}/api/delete_from_watchlist`, {
                                                    instrument_name: item
                                                });
                                                setWatchlistItems(prev => prev.filter(w => w !== item));
                                            } catch (err) {
                                                alert("Failed to delete from watchlist.");
                                                console.error(err);
                                            }
                                        }}
                                        style={{
                                            marginLeft: "10px",
                                            backgroundColor: "#dc3545",
                                            color: "white",
                                            border: "none",
                                            padding: "4px 8px",
                                            borderRadius: "4px",
                                            cursor: "pointer"
                                        }}
                                    >
                                        Delete
                                    </button>
                                </li>
                            ))}
                        </ul>
                        <div className="modal-actions">
                            <button onClick={() => setWatchlistModalOpen(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
            {isAddWatchlistModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>Add to Watchlist</h2>
                        <input
                            type="text"
                            placeholder="Format: EXCHANGE:STOCK"
                            value={newWatchlistEntry}
                            onChange={(e) => setNewWatchlistEntry(e.target.value)}
                            style={{ width: "100%", padding: "10px", marginBottom: "12px" }}
                        />
                        <div className="modal-actions">
                            <button onClick={() => setIsAddWatchlistModalOpen(false)}>Cancel</button>
                            <button
                                onClick={async () => {
                                    const input = newWatchlistEntry.trim();
                                    if (!/^[A-Z]+:[A-Z0-9]+$/i.test(input)) {
                                        alert("Invalid format. Use EXCHANGE:STOCK (e.g., NSE:TCS)");
                                        return;
                                    }
                                    try {
                                        const backendHost = window.location.hostname;
                                        const isSecure = window.location.protocol === "https:";
                                        const backendUrl = isSecure
                                            ? `https://${backendHost}`
                                            : `http://${backendHost}:5001`;

                                        const response = await axios.post(`${backendUrl}/api/append_to_watchlist`, {
                                            instrument_name: input
                                        });

                                        alert(response.data.message || "Instrument added.");
                                        setNewWatchlistEntry("");
                                        setIsAddWatchlistModalOpen(false);
                                    } catch (error) {
                                        console.error("Error adding to watchlist:", error);
                                        alert("Failed to add to watchlist.");
                                    }
                                }}
                            >
                                Add
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default Notifications;
